/**
 * Meta Ads — cheltuiala pe campanii, pe perioada aleasa (pasul 2 din specificatie).
 *
 * Cifrele vin din Meta, citite in fiecare noapte la 04:15 pe ultimele 7 zile (Meta corecteaza
 * retroactiv). Conversiile si valoarea lor sunt ALE LUI META, cu fereastra lui de atribuire —
 * ecranul o spune, ca nimeni sa nu le ia drept vanzarile noastre.
 *
 * Specificatia: `docs/superpowers/specs/2026-10-01-meta-ads-etapa1-design.md`.
 */
import { Fragment, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { QuickDateRange } from '../../components/ui/DateField'

interface Rand {
  id: number
  nume: string
  status: string | null
  cont: string
  cheltuiala: number
  afisari: number
  clicuri: number
  cpc: number | null
  conversiiMeta: number
  valoareMeta: number | null
}

interface Cont {
  id: number
  nume: string
  moneda: string
  ultimaSincronizareOk: string | null
  ultimaIncercare: string | null
  ultimaEroare: string | null
}

interface FacturaMeta {
  docNo: string
  data: string
  valoare: number
  platit: number
  serie: string | null
  legata: boolean
}

interface LunaFacturi {
  luna: string
  consumat: number
  facturat: number
  diferenta: number
  facturatNelegat: number
  facturi: FacturaMeta[]
}

const LUNI = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sep.',
  'oct.',
  'nov.',
  'dec.',
]
const numeLuna = (k: string) => `${LUNI[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`
const dmy = (iso: string) => iso.split('-').reverse().join('.')

/**
 * Facturile Meta din contabilitate, luna cu luna, langa cheltuiala citita din Meta.
 *
 * Diferenta pe luna in curs = consumat si inca nefacturat: suma pentru nota de factura nesosita.
 * Facturile unei serii fara cont conectat se arata separat — n-au cu ce se compara.
 */
function FacturiMeta({ de, pana }: { de: string; pana: string }) {
  const [deschisa, setDeschisa] = useState<string | null>(null)
  const q = useQuery({
    queryKey: ['meta-facturi', de, pana],
    queryFn: async () =>
      (
        await api.get<{ seriiConectate: string[]; luni: LunaFacturi[] }>('/meta/facturi', {
          params: { de, pana },
          timeout: 60_000,
        })
      ).data,
  })
  const d = q.data
  return (
    <Card
      title="Facturi Meta"
      description="Din contabilitate (ExpertAccounts), comparate cu cheltuiala citită din Meta."
    >
      {q.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se citesc facturile din ExpertAccounts…</p>
      ) : q.isError ? (
        <p className="text-sm text-b4l-red">
          {(q.error as { response?: { data?: { error?: { message?: string } } } }).response?.data
            ?.error?.message ?? 'Nu am putut citi facturile.'}
        </p>
      ) : !d || d.luni.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">Nicio factură Meta în perioada aleasă.</p>
      ) : (
        <div className="overflow-x-auto">
          {d.seriiConectate.length === 0 && (
            <p className="mb-2 text-xs text-b4l-amber">
              Niciun cont nu are seria facturilor completată (Meta → Setări Meta), deci toate
              facturile apar ca „cont nelegat”.
            </p>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                <th className="py-2">Luna</th>
                <th className="text-right">Consumat (Meta)</th>
                <th className="text-right">Facturat (EA)</th>
                <th
                  className="text-right"
                  title="Consumat minus facturat. Pe luna în curs: cheltuiala încă nefacturată."
                >
                  Diferență
                </th>
                <th
                  className="text-right"
                  title="Facturi ale unor conturi de reclame neconectate la CRM (ex. promovările din Instagram)"
                >
                  Cont nelegat
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.luni.map((l) => (
                <Fragment key={l.luna}>
                  <tr className="border-b border-b4l-border/60">
                    <td className="py-2">{numeLuna(l.luna)}</td>
                    <td className="text-right">{lei(l.consumat)}</td>
                    <td className="text-right">{lei(l.facturat)}</td>
                    <td
                      className={`text-right font-medium ${Math.abs(l.diferenta) >= 1 ? (l.diferenta > 0 ? 'text-b4l-amber' : 'text-b4l-red') : ''}`}
                    >
                      {lei(l.diferenta)}
                    </td>
                    <td className="text-right text-b4l-text-muted">
                      {l.facturatNelegat ? lei(l.facturatNelegat) : '—'}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="text-xs text-b4l-blue hover:underline"
                        onClick={() => setDeschisa(deschisa === l.luna ? null : l.luna)}
                      >
                        {deschisa === l.luna ? 'ascunde' : `${l.facturi.length} facturi`}
                      </button>
                    </td>
                  </tr>
                  {deschisa === l.luna && (
                    <tr>
                      <td colSpan={6} className="bg-b4l-bg px-3 py-2">
                        <table className="w-full text-xs">
                          <tbody>
                            {l.facturi.map((f) => (
                              <tr key={f.docNo}>
                                <td className="py-0.5 font-mono">{f.docNo}</td>
                                <td>{dmy(f.data)}</td>
                                <td className="text-right">{lei(f.valoare)}</td>
                                <td className="text-right text-b4l-text-muted">
                                  {f.platit >= f.valoare - 0.005
                                    ? 'plătită'
                                    : `plătit ${lei(f.platit)}`}
                                </td>
                                <td className="text-right text-b4l-text-muted">
                                  {f.legata ? '' : 'cont nelegat'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-b4l-text-muted">
            Meta facturează la praguri, nu pe lună, deci la lunile închise diferența e de obicei
            mică, nu zero. Pe luna în curs, diferența pozitivă e cheltuiala consumată și încă
            nefacturată — suma pentru nota de factură nesosită (408). Meta nu dă facturile prin API
            la plata cu cardul; ele vin din ExpertAccounts.
          </p>
        </div>
      )}
    </Card>
  )
}

interface Raspuns {
  de: string
  pana: string
  randuri: Rand[]
  total: {
    cheltuiala: number
    afisari: number
    clicuri: number
    conversiiMeta: number
    valoareMeta: number
  }
  conturi: Cont[]
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' lei'
const nr = (n: number) => n.toLocaleString('ro-RO')
/** Data locala `AAAA-LL-ZZ` — ca butoanele de perioada sa se recunoasca drept active. */
const ziua = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const cand = (s: string | null) =>
  s ? new Date(s).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' }) : '—'

/** Luna curenta, cap-coada — la fel ca butonul „Luna curentă", ca sa apara selectat. */
function lunaCurenta(): { de: string; pana: string } {
  const azi = new Date()
  return {
    de: ziua(new Date(azi.getFullYear(), azi.getMonth(), 1)),
    pana: ziua(new Date(azi.getFullYear(), azi.getMonth() + 1, 0)),
  }
}

/** „Toată perioada" vine cu datele goale; serverul vrea un interval. */
function interval(p: { de: string; pana: string }): { de: string; pana: string } {
  return { de: p.de || '2020-01-01', pana: p.pana || ziua(new Date()) }
}

/** Cardul cu o cifra si eticheta ei. `Card` arata descrierea doar langa un titlu. */
function Cifra({ eticheta, children }: { eticheta: string; children: ReactNode }) {
  return (
    <Card>
      <p className="text-xs text-b4l-text-muted">{eticheta}</p>
      <div className="mt-1 text-2xl font-semibold">{children}</div>
    </Card>
  )
}

export function MetaAdsPage() {
  const qc = useQueryClient()
  const poateScrie = useAuthStore((s) => s.permissions).includes('marketing.meta:write')
  const [perioada, setPerioada] = useState(lunaCurenta)

  const q = useQuery({
    queryKey: ['meta-cheltuiala', perioada.de, perioada.pana],
    queryFn: async () =>
      (await api.get<Raspuns>('/meta/cheltuiala', { params: interval(perioada) })).data,
  })

  const sync = useMutation({
    mutationFn: async (zile: number) =>
      (
        await api.post<{
          conturi: { nume: string; ok: boolean; randuri: number; eroare?: string }[]
        }>('/meta/sync', { zile }, { timeout: 180_000 })
      ).data,
    onSuccess: (r) => {
      const gresite = r.conturi.filter((c) => !c.ok)
      if (gresite.length) toast.error(gresite.map((c) => `${c.nume}: ${c.eroare}`).join(' · '))
      else toast.success(`Citit din Meta: ${r.conturi.reduce((t, c) => t + c.randuri, 0)} rânduri.`)
      void qc.invalidateQueries({ queryKey: ['meta-cheltuiala'] })
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? 'Sincronizarea a eșuat.',
      ),
  })

  const d = q.data
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Meta Ads</h1>
          <p className="text-sm text-b4l-text-muted">
            Cheltuiala pe campanii, citită din Meta în fiecare noapte la 04:15 (ultimele 7 zile —
            Meta mai corectează cifrele câteva zile după).
          </p>
        </div>
        <div className="flex items-end gap-2">
          {poateScrie && (
            <Button
              variant="secondary"
              loading={sync.isPending}
              onClick={() => sync.mutate(400)}
              title="Recitește din Meta ultimele 400 de zile, pe toate conturile urmărite"
            >
              Citește acum din Meta
            </Button>
          )}
        </div>
      </div>

      <QuickDateRange
        fromValue={perioada.de}
        toValue={perioada.pana}
        onChange={(de, pana) => setPerioada({ de, pana })}
      />

      {d && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Cifra eticheta="Cheltuit în perioadă">{lei(d.total.cheltuiala)}</Cifra>
          <Cifra eticheta="Clicuri · cost mediu pe clic">
            {nr(d.total.clicuri)}
            <span className="ml-2 text-sm font-normal text-b4l-text-muted">
              {d.total.clicuri > 0 ? lei(d.total.cheltuiala / d.total.clicuri) : '—'}
            </span>
          </Cifra>
          <Cifra eticheta="Afișări">{nr(d.total.afisari)}</Cifra>
          <Cifra eticheta="Cumpărări raportate de Meta · valoarea lor">
            {nr(d.total.conversiiMeta)}
            <span className="ml-2 text-sm font-normal text-b4l-text-muted">
              {lei(d.total.valoareMeta)}
            </span>
          </Cifra>
        </div>
      )}

      <Card>
        {q.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !d || d.randuri.length === 0 ? (
          <p className="text-sm text-b4l-text-muted">
            Nicio cheltuială în perioada aleasă.
            {d &&
              d.conturi.length === 0 &&
              ' Niciun cont de reclame urmărit — adaugă-l în Setări Meta.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                  <th className="py-2">Campanie</th>
                  <th>Cont</th>
                  <th>Stare</th>
                  <th className="text-right">Cheltuit</th>
                  <th className="text-right">Afișări</th>
                  <th className="text-right">Clicuri</th>
                  <th className="text-right">Cost/clic</th>
                  <th
                    className="text-right"
                    title="Cum le raportează Meta, cu fereastra lui de atribuire"
                  >
                    Cumpărări (Meta)
                  </th>
                  <th className="text-right" title="Cum o raportează Meta, nu vânzările noastre">
                    Valoare (Meta)
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.randuri.map((r) => (
                  <tr key={r.id} className="border-b border-b4l-border/60">
                    <td className="py-2">{r.nume}</td>
                    <td className="text-xs text-b4l-text-muted">{r.cont}</td>
                    <td className="text-xs text-b4l-text-muted">{r.status ?? '—'}</td>
                    <td className="text-right font-medium">{lei(r.cheltuiala)}</td>
                    <td className="text-right">{nr(r.afisari)}</td>
                    <td className="text-right">{nr(r.clicuri)}</td>
                    <td className="text-right">{r.cpc == null ? '—' : lei(r.cpc)}</td>
                    <td className="text-right">{nr(r.conversiiMeta)}</td>
                    <td className="text-right">
                      {r.valoareMeta == null ? '—' : lei(r.valoareMeta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-b4l-text-muted">
              Cumpărările și valoarea lor sunt cele raportate de Meta, cu fereastra lui de atribuire
              (7 zile după clic, 1 zi după vizualizare). Nu sunt vânzările din Shopify și sunt de
              regulă mai mari.
            </p>
          </div>
        )}
      </Card>

      <FacturiMeta {...interval(perioada)} />

      {d && d.conturi.length > 0 && (
        <Card title="Citirea din Meta, pe cont">
          <ul className="space-y-1 text-sm">
            {d.conturi.map((c) => (
              <li key={c.id} className="flex flex-wrap gap-x-3">
                <span className="font-medium">{c.nume}</span>
                <span className="text-b4l-text-muted">
                  ultima citire reușită: {cand(c.ultimaSincronizareOk)}
                </span>
                {c.ultimaEroare && (
                  <span className="text-b4l-red">
                    eroare la {cand(c.ultimaIncercare)}: {c.ultimaEroare}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
